import React, { useState, useRef, useCallback } from 'react';
import api from '../utils/api';

const THRESHOLD = 70;
const MAX_PULL = 110;
const REFRESH_BAR_HEIGHT = 48;

export default function PullToRefresh({ children }) {
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const startY = useRef(0);
  const pulling = useRef(false);

  const onTouchStart = useCallback((e) => {
    if (releasing || refreshing) return;
    const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
    if (scrollTop <= 0) {
      startY.current = e.touches[0].clientY;
      pulling.current = true;
    }
  }, [refreshing, releasing]);

  const onTouchMove = useCallback((e) => {
    if (!pulling.current || refreshing || releasing) return;
    const delta = e.touches[0].clientY - startY.current;
    if (delta > 0) {
      const distance = Math.min(delta * 0.4, MAX_PULL);
      setPullDistance(distance);
    } else {
      pulling.current = false;
      setPullDistance(0);
    }
  }, [refreshing, releasing]);

  const onTouchEnd = useCallback(() => {
    if (!pulling.current) return;
    pulling.current = false;
    if (pullDistance >= THRESHOLD) {
      setRefreshing(true);
      setReleasing(true);
      setPullDistance(REFRESH_BAR_HEIGHT);
      // Sync data from TikTok, then reload page
      api.post('/api/dashboard/sync').catch(() => {}).finally(() => {
        window.location.reload();
      });
    } else {
      setReleasing(true);
      setPullDistance(0);
      setTimeout(() => setReleasing(false), 250);
    }
  }, [pullDistance]);

  const progress = Math.min(pullDistance / THRESHOLD, 1);

  return (
    <div onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
      style={{ minHeight: '100vh' }}>
      {/* Refresh indicator bar */}
      <div style={{
        height: refreshing ? `${REFRESH_BAR_HEIGHT}px` : pullDistance > 0 ? `${pullDistance}px` : 0,
        background: '#1a365d',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
        overflow: 'hidden',
        transition: pulling.current ? 'none' : 'height 0.25s cubic-bezier(0.2, 0, 0, 1)',
        position: refreshing ? 'sticky' : 'relative',
        top: 0,
        zIndex: 9999,
      }}>
        {refreshing ? (
          <>
            <div style={{
              width: '20px', height: '20px', borderRadius: '50%',
              border: '2.5px solid rgba(255,255,255,0.25)',
              borderTopColor: 'white',
              animation: 'spin 0.6s linear infinite',
              flexShrink: 0,
            }} />
            <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '13px', fontWeight: 500 }}>
              Syncing data...
            </span>
          </>
        ) : (
          <svg width="22" height="22" viewBox="0 0 22 22"
            style={{ opacity: progress, transform: `rotate(${pullDistance * 4}deg)`,
              transition: pulling.current ? 'none' : 'opacity 0.2s, transform 0.2s' }}>
            <circle cx="11" cy="11" r="9" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="2.5" />
            <circle cx="11" cy="11" r="9" fill="none" stroke="white" strokeWidth="2.5"
              strokeDasharray={`${progress * 56.5} 56.5`} strokeLinecap="round"
              transform="rotate(-90 11 11)" />
          </svg>
        )}
      </div>
      {children}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
